import { Moon, Sun } from "@phosphor-icons/react";
import { useTheme } from "@charisol-spark/ui";
import logoSvg from "./assets/SparkLogo.svg";

/**
 * Footer link columns. A link without an `href` has no destination yet: it
 * renders greyed out as "(coming soon)" rather than as a dead `#` link.
 * Tracked in https://github.com/Challaris/charisol-spark-backend/issues/182.
 */
const FOOTER_COLUMNS: {
  title: string;
  links: { label: string; href?: string }[];
}[] = [
  {
    title: "Product",
    links: [
      { label: "Features", href: "/#features" },
      { label: "How It Works", href: "/#how-it-works" },
      { label: "Pricing", href: "/pricing" },
      { label: "API" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", href: "https://charisol.io" },
      { label: "Blog" },
      { label: "Careers" },
      { label: "Contact", href: "https://charisol.io/get-started/" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Documentation" },
      { label: "Help Center" },
      { label: "Community" },
      { label: "Status" },
    ],
  },
];

/**
 * Public site footer shared between the landing page and public marketing
 * pages (e.g. /pricing). Includes logo, link columns, copyright, and the
 * light/dark/system theme toggle.
 */
export function LandingFooter() {
  const { theme, setTheme } = useTheme();

  return (
    <footer className="py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <img src={logoSvg} alt="Spark Logo" className="h-20" />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Share your perspective.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Turn your experiences and opinions into posts that sound like you,
              and keep showing up.
            </p>
          </div>

          {FOOTER_COLUMNS.map((column) => (
            <div key={column.title}>
              <h4 className="font-medium mb-4 text-sm">{column.title}</h4>
              <ul className="space-y-3">
                {column.links.map((item) => (
                  <li key={item.label}>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <span
                        aria-disabled="true"
                        className="text-sm text-muted-foreground/50 cursor-default"
                      >
                        {item.label}{" "}
                        <span className="text-xs">(coming soon)</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-border/30 gap-4">
          <div className="text-sm text-muted-foreground">
            <p className="text-lg mb-1">
              <a href="https://charisol.io">
                <strong>
                  By <span className="font-black">CHARISOL</span>
                </strong>
              </a>
            </p>
            <p>
              © Charisol {new Date().getFullYear()}. All Rights Reserved |{" "}
              <a href="/terms">
                <strong>Terms</strong>
              </a>{" "}
              |{" "}
              <a href="/privacy">
                <strong>Privacy</strong>
              </a>{" "}
              |{" "}
              <a href="/data-deletion">
                <strong>Data deletion</strong>
              </a>{" "}
              |{" "}
              <a href="https://charisol.io/get-started/">
                <strong>Contact Us</strong>
              </a>
            </p>
          </div>

          {/* Theme Toggle */}
          <div
            className={`flex items-center gap-1 rounded-full p-1 ${
              theme === "dark" ? "bg-[#2a2a2a]" : "bg-muted"
            }`}
          >
            <button
              onClick={() => setTheme("system")}
              className={`p-2 rounded-full transition-all duration-200 ${
                theme === "system"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label="System theme"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </button>
            <button
              onClick={() => setTheme("light")}
              className={`p-2 rounded-full transition-all duration-200 ${
                theme === "light"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label="Light theme"
            >
              <Sun className="w-4 h-4" />
            </button>
            <button
              onClick={() => setTheme("dark")}
              className={`p-2 rounded-full transition-all duration-200 ${
                theme === "dark"
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-label="Dark theme"
            >
              <Moon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
